import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import Text from 'core-system/Text'
import { Location } from 'core-system/Icons/Misc'

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
`

const WelcomeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const Subtitle = styled(Text)`
  color: ${(props) => props.theme.palette.text.placeholder};
  font-size: 0.875rem;
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const OfficeSelectorWrapper = styled.div`
  position: relative;
`

const OfficeSelector = styled.div<{ isOpen?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border-radius: 0.5rem;
  border: 1px solid
    ${(props) =>
      props.isOpen
        ? props.theme.palette.grey.grey400
        : props.theme.palette.grey.grey300};
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) => props.theme.palette.white};

  &:hover {
    border-color: ${(props) => props.theme.palette.grey.grey400};
    background-color: ${(props) => props.theme.palette.grey.grey50};
  }
`

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 100%;
  background-color: ${(props) => props.theme.palette.white};
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  border-radius: 0.5rem;
  box-shadow: ${(props) => props.theme.dropShadows.normal};
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  transform: ${(props) =>
    props.isOpen ? 'translateY(0)' : 'translateY(-0.5rem)'};
  transition: all 0.2s ease;
  z-index: 100;
  overflow: hidden;
`

const DropdownItem = styled.div<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  cursor: pointer;
  background-color: ${(props) =>
    props.isSelected ? props.theme.palette.grey.grey50 : 'transparent'};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${(props) => props.theme.palette.grey.grey100};
  }
`

const CheckIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M20 6L9 17L4 12'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const CheckPlaceholder = styled.div`
  width: 14px;
  height: 14px;
`

const LocationIcon = styled.div`
  color: ${(props) => props.theme.palette.primary.pink500};
  display: flex;
  align-items: center;
`

const ChevronDown = () => (
  <svg
    width='12'
    height='12'
    viewBox='0 0 12 12'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M3 4.5L6 7.5L9 4.5'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

// Mock office locations for demo
const OFFICE_LOCATIONS = [
  'San Francisco, CA',
  'New York, NY',
  'Los Angeles, CA',
  'Seattle, WA',
]

const EmployeeDashboardPageHeader: React.FC = () => {
  const { profileData } = useSelector(
    (state: AppState) => state.employeePlatform
  )
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedOffice, setSelectedOffice] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const firstName = profileData?.firstName || 'there'

  // Get office location - extract city and state or use fallback
  const getOfficeLocation = () => {
    if (selectedOffice) {
      return selectedOffice
    }
    if (profileData?.worksiteAddress) {
      // Ensure proper spacing after commas
      return profileData.worksiteAddress.replace(/,(?!\s)/g, ', ')
    }
    return 'San Francisco, CA'
  }

  const currentOffice = getOfficeLocation()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleOfficeSelect = (office: string) => {
    setSelectedOffice(office)
    setIsDropdownOpen(false)
  }

  // Get current month name
  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long' })

  return (
    <HeaderContainer>
      <WelcomeSection>
        <Text variant='h2'>Welcome back, {firstName}</Text>
        <Subtitle>Here's your commute snapshot for {currentMonth}</Subtitle>
      </WelcomeSection>

      <RightSection>
        <OfficeSelectorWrapper ref={dropdownRef}>
          <OfficeSelector
            isOpen={isDropdownOpen}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <LocationIcon>
              <Location width={16} height={16} />
            </LocationIcon>
            <Text variant='action4'>{currentOffice}</Text>
            <ChevronDown />
          </OfficeSelector>
          <DropdownMenu isOpen={isDropdownOpen}>
            {OFFICE_LOCATIONS.map((office) => (
              <DropdownItem
                key={office}
                isSelected={office === currentOffice}
                onClick={() => handleOfficeSelect(office)}
              >
                {office === currentOffice ? (
                  <CheckIcon />
                ) : (
                  <CheckPlaceholder />
                )}
                <Text variant='body2'>{office}</Text>
              </DropdownItem>
            ))}
          </DropdownMenu>
        </OfficeSelectorWrapper>
      </RightSection>
    </HeaderContainer>
  )
}

export default React.memo(EmployeeDashboardPageHeader)
